import * as React from 'react'
import { Box, Button, TextField, Typography, useMediaQuery } from '@mui/material'
import { useTheme } from '@mui/material/styles'

import logo from '../assets/images/logo-lg.svg'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { usePostResetPasswordMutation, useGetUserMutation } from '../redux/api/userApi'
import { useLoginMutation } from '../redux/api/authApi'
import { useEffect } from 'react'
import ErrorMessage from '../components/ErrorMessage'
import useToken from '../hooks/useToken'
import { PATH, PATH_LOGIN } from '../routes/constant'
import { useSelector } from 'react-redux/es/hooks/useSelector'
import { type IUserProfile } from '../redux/api/types'
import { useFormik } from 'formik'
import { FORMIK_VALUE, VALIDATION_SCHEMA } from '../components/createPassword/consts'

const PasswordCreatePage: React.FC = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'))
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { xsrf, isLoading: isLoadingXsrf } = useToken()
  const logged = useSelector((state: { userState: { user: IUserProfile } }) => state.userState.user)

  const email = searchParams.get('email')
  const resetToken = searchParams.get('token')
  const [resetPassword, {
    isLoading,
    isSuccess,
    error,
    isError
  }] = usePostResetPasswordMutation()
  const [password, setPassword] = React.useState('')

  const formikCreatePassword = useFormik({
    enableReinitialize: true,
    validateOnChange: true,
    initialValues: {
      [FORMIK_VALUE.PASSWORD]: '',
      [FORMIK_VALUE.CONFIRM_PASSWORD]: ''
    },
    validationSchema: VALIDATION_SCHEMA,
    onSubmit: async (values) => {
      setPassword(values[FORMIK_VALUE.PASSWORD])
      await resetPassword({
        email,
        password: values[FORMIK_VALUE.PASSWORD],
        passwordConfirmation: values[FORMIK_VALUE.CONFIRM_PASSWORD],
        resetToken,
        xsrf
      }
      )
    }
  })

  const submit = async (e: any) => {
    e.preventDefault()
    await formikCreatePassword.submitForm()
  }

  const config = [
    {
      id: 0,
      label: 'Password',
      formikName: FORMIK_VALUE.PASSWORD
    },
    {
      id: 1,
      label: 'Confirm Password',
      formikName: FORMIK_VALUE.CONFIRM_PASSWORD
    }
  ]

  const [login, {
    isLoading: isLoginLoading,
    isSuccess: isLoginSuccess,
    isError: isLoginError
  }] = useLoginMutation()
  const [getUser, {
    isLoading: isUserLoading,
    isSuccess: isUserSuccess,
    isError: isUserError
  }] = useGetUserMutation()

  useEffect(() => {
    if (isSuccess) {
      login({
        email,
        password,
        token: xsrf
      }).then(() => {})
        .catch(() => {})
    }
  }, [isSuccess])

  useEffect(() => {
    if (isLoginSuccess) {
      getUser().then(() => {}).catch(() => {})
    }
    if (isLoginError) {
      navigate(PATH_LOGIN.LOGIN, { replace: true, state: { isPasswordChange: true } })
    }
  }, [isLoginLoading])

  useEffect(() => {
    if (isUserSuccess) {
      navigate(PATH.HOME, { replace: true })
    }
    if (isUserError) {
      navigate(PATH_LOGIN.LOGIN, { replace: true, state: { isPasswordChange: true } })
    }
  }, [isUserLoading])

  useEffect(() => {
    if (logged) {
      navigate(PATH.HOME, { replace: true })
    }
  }, [logged])

  return (
        <Box>
            <Box
                component='img'
                src={logo}
                sx={{
                  display: 'block',
                  height: '70px',
                  margin: '-80px auto 50px',
                  width: '189px'
                }}
            />
            <Typography sx={{
              color: '#000',
              fontSize: 18,
              mb: '27px',
              textAlign: isMobile ? 'left' : 'left'
            }} variant='h2'>Create a password to continue to your new Agent Elite Portal</Typography>
            <form onSubmit={submit} noValidate>
                {config.map((el) => {
                  return <label key={el.id}>
                        <Typography sx={{
                          fontFamily: 'Roboto',
                          fontSize: '14px',
                          fontWeight: 700,
                          lineHeight: '30px',
                          marginBottom: '8px'
                        }}>
                            {el.label}
                        </Typography>
                        <TextField
                            error={!!(formikCreatePassword.touched[el.formikName] &&
                                formikCreatePassword.errors?.[el.formikName])}
                            aria-invalid={isError}
                            onBlur={async (e) => {
                              formikCreatePassword.handleBlur(e)
                              await formikCreatePassword.setFieldTouched(el.formikName, true)
                            }}
                            onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
                              await formikCreatePassword.setFieldValue(el.formikName, e.currentTarget.value)
                            }}
                            value={formikCreatePassword.values[el.formikName]}
                            type='password'
                            fullWidth
                        />
                        {formikCreatePassword.touched[el.formikName] &&
                            formikCreatePassword.errors?.[el.formikName] &&
                            <Typography sx={{
                              marginTop: '10px',
                              color: '#E84419'
                            }}>
                                {formikCreatePassword.errors?.[el.formikName]}
                            </Typography>}
                    </label>
                })}
                <Button disabled={!formikCreatePassword.isValid || isLoadingXsrf || isLoading} sx={{
                  border: '2px solid #09B5EC',
                  background: '#09B5EC',
                  width: '100%',
                  height: '52px',
                  marginTop: '25px',
                  '&:hover': {
                    background: '#09B5EC'
                  },
                  '&:disabled': {
                    color: 'rgba(0, 0, 0, 0.26)',
                    border: '1px solid rgba(0, 0, 0, 0.12)',
                    background: 'inherit'
                  }
                }} type='submit'>Continue</Button>
            </form>
            <ErrorMessage error={error}/>
        </Box>
  )
}

export default PasswordCreatePage
